import { useEffect, useState, useCallback, useMemo } from "react";
import {
  onSnapshot,
  query,
  collection,
  where,
  documentId,
} from "firebase/firestore";
import { db } from "config/firebase";

const chunkArray = (array, size) => {
  return array.length > size
    ? [array.slice(0, size), ...chunkArray(array.slice(size), size)]
    : [array];
};

const useMainPage = ({ predictions, promises, predictors, surveys }) => {
  const [updatedPredictions, setUpdatedPredictions] = useState([]);
  const [updatedPromises, setUpdatedPromises] = useState([]);
  const [updatedPredictors, setUpdatedPredictors] = useState([]);
  const [updatedSurveys, setUpdatedSurveys] = useState([]);

  const subscribeToCollection = useCallback((collectionName, ids, setState) => {
    if (!ids.length) return;

    const chunks = chunkArray(ids, 10); // Firestore `in` limitation

    const unsubscribes = chunks.map((idChunk) => {
      const q = query(
        collection(db, collectionName),
        where(documentId(), "in", idChunk),
      );

      return onSnapshot(q, (querySnapshot) => {
        setState((prev) => {
          const updatedDocs = querySnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));

          // Create a lookup object for fast replacement
          const updatedDocsMap = new Map(
            updatedDocs.map((doc) => [doc.id, doc]),
          );

          // Maintain the original order of `ids`
          return ids
            .map(
              (id) =>
                updatedDocsMap.get(id) ||
                prev.find((doc) => doc.id === id) ||
                null,
            )
            .filter(Boolean);
        });
      });
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, []);

  const predictionIds = useMemo(
    () => predictions.map((p) => p.id),
    [predictions],
  );
  useEffect(() => {
    return subscribeToCollection(
      "predictions",
      predictionIds,
      setUpdatedPredictions,
    );
  }, [predictionIds, subscribeToCollection]);

  const promiseIds = useMemo(
    () => promises?.map((p) => p.id) || [],
    [promises],
  );
  useEffect(() => {
    return subscribeToCollection("promises", promiseIds, setUpdatedPromises);
  }, [promiseIds, subscribeToCollection]);

  const predictorIds = useMemo(() => predictors.map((p) => p.id), [predictors]);
  useEffect(() => {
    return subscribeToCollection(
      "predictors",
      predictorIds,
      setUpdatedPredictors,
    );
  }, [predictorIds, subscribeToCollection]);

  const surveyIds = useMemo(() => surveys.map((p) => p.id), [surveys]);
  useEffect(() => {
    return subscribeToCollection("surveys", surveyIds, setUpdatedSurveys);
  }, [surveyIds, subscribeToCollection]);

  return {
    updatedPredictions,
    updatedPromises,
    updatedPredictors,
    updatedSurveys,
  };
};

export default useMainPage;
